/*!
 * Cardio Online — Collection Faceted Filter Behaviour (v2.0)
 *
 * v2.0 changes (2026-08-12):
 *   - STAGED APPLY. Ticking a box or typing a price no longer fires a fetch.
 *     Selections are held until the customer presses "Apply filters", so a
 *     multi-part change (price From + To) can be finished before anything moves.
 *   - Revert / Clear all. Revert discards unapplied edits and restores the form
 *     to the live filter set; Clear all drops every applied filter.
 *   - Delegated event binding. v1.0 bound `change` to the form element, then
 *     replaced that element on every AJAX swap — so the sidebar went dead after
 *     the first filter. Everything now delegates off `document` and survives.
 *   - Accordion state, sidebar scroll position and focus are preserved across a
 *     swap instead of resetting to the server defaults.
 *
 * Still handles: AJAX grid update, URL sync via pushState, mobile drawer with
 * focus trap, active-filter chips, skeleton loading state.
 *
 * Coexists with z__jsSidebar.js — that script binds `change` on the form and
 * does `window.location.search = …` (full reload). We intercept in the capture
 * phase at document level and stopPropagation, so it never runs.
 */
(function () {
  'use strict';

  var GRID_SELECTOR = '[data-collection-main]';
  var FORM_SELECTOR = '[data-faceted-filter-form]';
  var SORT_SELECTOR = '[data-cos-sort]';
  var DRAWER_SELECTOR = '[data-cos-drawer]';
  var DRAWER_BODY_SELECTOR = '[data-cos-drawer-body]';
  var DRAWER_TRIGGER_SELECTOR = '[data-cos-drawer-trigger]';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function getForm() { return $(FORM_SELECTOR); }
  function getGrid() { return $(GRID_SELECTOR); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function closestFrom(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  // -------------------------------------------------------------------------
  // Applied vs staged state
  // -------------------------------------------------------------------------
  // "applied"  = the filter params currently in the address bar (what the grid shows)
  // "staged"   = whatever the form controls say right now
  // pending    = the two disagree, i.e. there are unapplied changes
  function sigFrom(params) {
    var pairs = [];
    params.forEach(function (v, k) {
      if (k.indexOf('filter.') === 0 && v !== '') pairs.push(k + '=' + v);
    });
    pairs.sort();
    return pairs.join('&');
  }

  function appliedParams() { return new URLSearchParams(window.location.search); }
  function appliedSig() { return sigFrom(appliedParams()); }

  function stagedSig() {
    var form = getForm();
    if (!form) return appliedSig();
    return sigFrom(new URLSearchParams(new FormData(form)));
  }

  function isPending() { return stagedSig() !== appliedSig(); }

  function activeCount() {
    var n = 0;
    appliedParams().forEach(function (v, k) {
      if (k.indexOf('filter.') === 0 && v !== '') n += 1;
    });
    return n;
  }

  function buildUrlFromForm(form) {
    var out = new URLSearchParams();
    // 1) staged filter params from the form
    new URLSearchParams(new FormData(form)).forEach(function (v, k) {
      if (v !== '') out.append(k, v);
    });
    // 2) preserve non-filter params (sort_by etc). Drop `page` — a new filter
    //    set should always land the customer back on page 1.
    appliedParams().forEach(function (v, k) {
      if (k.indexOf('filter.') !== 0 && k !== 'page') out.append(k, v);
    });
    var qs = out.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  // -------------------------------------------------------------------------
  // Screen-reader announcements
  // -------------------------------------------------------------------------
  var liveRegion = null;
  function announce(msg) {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'cos-visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = msg;
  }

  // -------------------------------------------------------------------------
  // Action bar (Apply / Revert / Clear all)
  // -------------------------------------------------------------------------
  var busy = false;

  function updateActions() {
    var pending = isPending();
    var hasActive = activeCount() > 0;

    $$('[data-cos-actions]').forEach(function (bar) {
      bar.classList.toggle('is-visible', pending || hasActive);
      var apply = $('[data-cos-apply]', bar);
      var revert = $('[data-cos-revert]', bar);
      var clear = $('[data-cos-clear]', bar);
      var hint = $('[data-cos-hint]', bar);
      if (apply) apply.disabled = !pending || busy;
      if (revert) revert.hidden = !pending;
      if (clear) clear.hidden = pending || !hasActive;
      if (hint) hint.hidden = !pending;
    });

    // Mobile drawer footer mirrors the same state
    var dApply = $('[data-cos-drawer-apply]');
    var dRevert = $('[data-cos-drawer-revert]');
    var dClear = $('[data-cos-drawer-clear]');
    if (dApply) dApply.textContent = pending ? 'Apply filters' : 'Show results';
    if (dRevert) dRevert.hidden = !pending;
    if (dClear) dClear.hidden = pending || !hasActive;
  }

  function setBusy(state) {
    busy = state;
    $$('[data-cos-actions]').forEach(function (bar) {
      bar.classList.toggle('is-busy', state);
    });
    updateActions();
  }

  function applyStaged() {
    var form = getForm();
    if (!form) return;
    if (!isPending()) return;
    fetchAndSwap(buildUrlFromForm(form), true);
  }

  function revertStaged() {
    var form = getForm();
    if (!form) return;
    var p = appliedParams();
    $$('input[type="checkbox"]', form).forEach(function (cb) {
      cb.checked = p.getAll(cb.name).indexOf(cb.value) > -1;
    });
    $$('.cos-facet__price input', form).forEach(function (inp) {
      inp.value = p.get(inp.name) || '';
    });
    updateActions();
    announce('Unapplied filter changes reverted.');
  }

  function clearAll() {
    var out = new URLSearchParams();
    appliedParams().forEach(function (v, k) {
      if (k.indexOf('filter.') !== 0 && k !== 'page') out.append(k, v);
    });
    var qs = out.toString();
    fetchAndSwap(window.location.pathname + (qs ? '?' + qs : ''), true);
  }

  // -------------------------------------------------------------------------
  // AJAX fetch + DOM swap
  // -------------------------------------------------------------------------
  var inflight = null;

  function swapNode(oldEl, newEl) {
    if (oldEl && newEl && oldEl.parentNode) oldEl.parentNode.replaceChild(newEl, oldEl);
  }

  function captureState() {
    var st = { open: {}, focusId: null, sidebarScroll: 0, drawerScroll: 0 };
    $$('.cos-facet__toggle').forEach(function (b) {
      if (b.id) st.open[b.id] = b.getAttribute('aria-expanded') === 'true';
    });
    var ae = document.activeElement;
    if (ae && ae.id) st.focusId = ae.id;
    var sb = $('.sidebar-section');
    if (sb) st.sidebarScroll = sb.scrollTop;
    var db = $(DRAWER_BODY_SELECTOR);
    if (db) st.drawerScroll = db.scrollTop;
    return st;
  }

  function restoreState(st) {
    Object.keys(st.open).forEach(function (id) {
      var b = document.getElementById(id);
      if (!b) return;
      b.setAttribute('aria-expanded', st.open[id] ? 'true' : 'false');
      var p = b.closest('.cos-facet');
      if (p) p.classList.toggle('is-open', st.open[id]);
    });
    var sb = $('.sidebar-section');
    if (sb) sb.scrollTop = st.sidebarScroll;
    var db = $(DRAWER_BODY_SELECTOR);
    if (db) db.scrollTop = st.drawerScroll;
    if (st.focusId) {
      var el = document.getElementById(st.focusId);
      if (el && typeof el.focus === 'function') {
        try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
      }
    }
  }

  function syncDrawerBadge(doc) {
    var oldBadge = $('[data-cos-drawer-badge]');
    var newBadge = doc.querySelector('[data-cos-drawer-badge]');
    if (oldBadge && newBadge) { swapNode(oldBadge, newBadge); return; }
    if (oldBadge && !newBadge) { oldBadge.parentNode.removeChild(oldBadge); return; }
    if (!oldBadge && newBadge) {
      var trigger = $(DRAWER_TRIGGER_SELECTOR);
      if (trigger) trigger.appendChild(newBadge.cloneNode(true));
    }
  }

  function fetchAndSwap(url, pushHistory) {
    var grid = getGrid();
    if (!grid) { window.location.assign(url); return; }

    grid.classList.add('is-cos-loading');
    setBusy(true);

    if (inflight) inflight.abort();
    if (window.AbortController) inflight = new AbortController();
    var signal = inflight ? inflight.signal : undefined;

    var state = captureState();

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // Product grid + pagination
        var newGrid = doc.querySelector(GRID_SELECTOR);
        if (newGrid) {
          swapNode(
            grid.querySelector('.collection-matrix__wrapper'),
            newGrid.querySelector('.collection-matrix__wrapper')
          );
          swapNode(
            grid.querySelector('.container--pagination'),
            newGrid.querySelector('.container--pagination')
          );
        }

        // Sidebar form (refreshes per-option counts and checked states)
        swapNode(getForm(), doc.querySelector(FORM_SELECTOR));

        // Chip strip + result count
        ['[data-cos-active-chips]', '[data-cos-result-count]'].forEach(function (sel) {
          swapNode($(sel), doc.querySelector(sel));
        });
        syncDrawerBadge(doc);

        // Keep the sort dropdown showing the active sort
        var sortEl = $(SORT_SELECTOR);
        if (sortEl) {
          var sortParam = new URLSearchParams(url.split('?')[1] || '').get('sort_by');
          if (sortParam) sortEl.value = sortParam;
        }

        if (pushHistory) window.history.pushState({ cos: true }, '', url);

        syncDrawerWithSidebar();
        restoreState(state);
        updateActions();

        var count = $('[data-cos-result-count]');
        announce(
          count ? count.textContent.replace(/\s+/g, ' ').trim() : 'Filters updated.'
        );

        document.dispatchEvent(new CustomEvent('cos:filtered', { detail: { url: url } }));
        if (window.PXUTheme && typeof window.PXUTheme.refreshTheme === 'function') {
          try { window.PXUTheme.refreshTheme(); } catch (e) { /* noop */ }
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; // superseded by a newer fetch
        window.location.assign(url); // hard fallback
      })
      .then(function () {
        var g = getGrid();
        if (g) g.classList.remove('is-cos-loading');
        setBusy(false);
      });
  }

  // -------------------------------------------------------------------------
  // Delegated form handling — survives every AJAX swap
  // -------------------------------------------------------------------------
  function bindFormDelegates() {
    // Capture phase at document level: pre-empts the legacy z__jsSidebar
    // listener bound on the form element, which would do a full page reload.
    document.addEventListener('change', function (e) {
      if (!closestFrom(e.target, FORM_SELECTOR)) return;
      e.stopPropagation();
      updateActions();
    }, true);

    // Live feedback while typing a price, before blur fires `change`
    document.addEventListener('input', function (e) {
      if (!closestFrom(e.target, FORM_SELECTOR)) return;
      if (e.target.classList.contains('cos-facet__search')) return; // in-list search
      updateActions();
    }, true);

    // Enter anywhere in the form applies (except on buttons / the option search)
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      if (!closestFrom(e.target, FORM_SELECTOR)) return;
      if (e.target.tagName === 'BUTTON') return;
      e.preventDefault();
      if (e.target.classList.contains('cos-facet__search')) return;
      applyStaged();
    }, true);

    document.addEventListener('submit', function (e) {
      if (!closestFrom(e.target, FORM_SELECTOR)) return;
      e.preventDefault();
      applyStaged();
    }, true);
  }

  function bindClicks() {
    document.addEventListener('click', function (e) {
      var t = e.target;

      var apply = closestFrom(t, '[data-cos-apply]');
      if (apply) { e.preventDefault(); applyStaged(); return; }

      var revert = closestFrom(t, '[data-cos-revert], [data-cos-drawer-revert]');
      if (revert) { e.preventDefault(); revertStaged(); return; }

      var clear = closestFrom(t, '[data-cos-clear]');
      if (clear) { e.preventDefault(); clearAll(); return; }

      var drawerClear = closestFrom(t, '[data-cos-drawer-clear]');
      if (drawerClear) { e.preventDefault(); clearAll(); closeDrawer(); return; }

      var drawerApply = closestFrom(t, '[data-cos-drawer-apply]');
      if (drawerApply) { e.preventDefault(); applyStaged(); closeDrawer(); return; }

      var chip = closestFrom(t, '[data-cos-chip-remove]');
      if (chip) { e.preventDefault(); fetchAndSwap(chip.getAttribute('href'), true); return; }

      // Accordion toggle (delegated so it survives swaps)
      var toggle = closestFrom(t, '.cos-facet__toggle');
      if (toggle) {
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var facet = toggle.closest('.cos-facet');
        if (facet) facet.classList.toggle('is-open', !expanded);
      }
    });
  }

  // Per-facet option search (in-list filter) — delegated
  function bindOptionSearch() {
    document.addEventListener('input', function (e) {
      var input = e.target;
      if (!input.classList || !input.classList.contains('cos-facet__search')) return;
      var q = input.value.trim().toLowerCase();
      var inner = input.closest('.cos-facet__panel-inner');
      var list = inner ? inner.querySelector('.cos-facet__list') : null;
      if (!list) return;
      $$('.cos-facet__option', list).forEach(function (li) {
        var label = li.querySelector('.cos-facet__option-text');
        var text = label ? label.textContent.toLowerCase() : '';
        li.style.display = (!q || text.indexOf(q) > -1) ? '' : 'none';
      });
    });
  }

  // -------------------------------------------------------------------------
  // Sort select — applies immediately (a sort is a single, complete decision)
  // -------------------------------------------------------------------------
  function bindSort() {
    var sort = $(SORT_SELECTOR);
    if (!sort || sort.dataset.cosBound === '1') return;
    sort.dataset.cosBound = '1';
    sort.addEventListener('change', function (e) {
      // Stop z__jsCollection.js also firing — it empties the whole toolbar.
      e.stopImmediatePropagation();
      var existing = appliedParams();
      existing.set('sort_by', sort.value);
      existing.delete('page');
      fetchAndSwap(window.location.pathname + '?' + existing.toString(), true);
    }, true);
  }

  // -------------------------------------------------------------------------
  // Mobile drawer
  // -------------------------------------------------------------------------
  var lastFocus = null;

  function openDrawer() {
    var drawer = $(DRAWER_SELECTOR);
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var trigger = $(DRAWER_TRIGGER_SELECTOR);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var closeBtn = drawer.querySelector('[data-cos-drawer-close]');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', onDrawerKey);
  }

  function closeDrawer() {
    var drawer = $(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    var trigger = $(DRAWER_TRIGGER_SELECTOR);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    document.removeEventListener('keydown', onDrawerKey);
  }

  function onDrawerKey(e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var drawer = $(DRAWER_SELECTOR);
    if (!drawer) return;
    var focusables = $$(
      'button:not([hidden]):not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      drawer
    ).filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function bindDrawer() {
    var trigger = $(DRAWER_TRIGGER_SELECTOR);
    if (trigger && trigger.dataset.cosBound !== '1') {
      trigger.dataset.cosBound = '1';
      trigger.addEventListener('click', function () {
        trigger.getAttribute('aria-expanded') === 'true' ? closeDrawer() : openDrawer();
      });
    }
    var drawer = $(DRAWER_SELECTOR);
    if (!drawer) return;
    $$('[data-cos-drawer-close]', drawer).forEach(function (el) {
      if (el.dataset.cosBound === '1') return;
      el.dataset.cosBound = '1';
      el.addEventListener('click', closeDrawer);
    });
  }

  // On mobile, teleport the sidebar form into the drawer body so a single
  // source of truth drives both desktop sidebar and mobile drawer.
  function syncDrawerWithSidebar() {
    var drawerBody = $(DRAWER_BODY_SELECTOR);
    var form = getForm();
    if (!drawerBody || !form) return;
    if (drawerBody.contains(form)) return;
    if (window.matchMedia('(max-width: 798px)').matches) drawerBody.appendChild(form);
  }

  // -------------------------------------------------------------------------
  // Back/forward
  // -------------------------------------------------------------------------
  function bindHistory() {
    window.addEventListener('popstate', function () {
      fetchAndSwap(window.location.pathname + window.location.search, false);
    });
  }

  // -------------------------------------------------------------------------
  // Sticky mobile toolbar offset (unchanged from v1.0)
  // -------------------------------------------------------------------------
  function setMobileHeaderOffset() {
    var docEl = document.documentElement;
    if (window.innerWidth >= 799) {
      docEl.style.removeProperty('--cos-mobile-sticky-offset');
      return;
    }
    var sel = [
      '.header__inner-content',
      '.header__icons',
      '.header-section .header__wrapper',
      '.header__wrapper',
      '.site-header',
      '.header-section'
    ];
    var chosen = 0;
    for (var i = 0; i < sel.length; i++) {
      var el = document.querySelector(sel[i]);
      if (!el) continue;
      var r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      if (r.height > 140) continue;
      if (r.bottom > 0 && r.bottom <= 140) { chosen = r.bottom; break; }
    }
    if (!chosen) chosen = 76;
    docEl.style.setProperty('--cos-mobile-sticky-offset', Math.max(0, Math.floor(chosen)) + 'px');
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------
  function init() {
    bindFormDelegates();
    bindClicks();
    bindOptionSearch();
    bindSort();
    bindDrawer();
    bindHistory();
    syncDrawerWithSidebar();
    updateActions();
    setMobileHeaderOffset();

    window.addEventListener('resize', debounce(setMobileHeaderOffset, 150));
    window.addEventListener('scroll', debounce(setMobileHeaderOffset, 250), { passive: true });
    window.addEventListener('resize', debounce(function () {
      var drawer = $(DRAWER_SELECTOR);
      if (drawer && drawer.getAttribute('aria-hidden') === 'false' && window.innerWidth >= 799) {
        closeDrawer();
      }
      var form = getForm();
      var drawerBody = $(DRAWER_BODY_SELECTOR);
      var sidebar = $('.sidebar-section aside, .sidebar-section');
      if (window.innerWidth >= 799 && drawerBody && form && drawerBody.contains(form) && sidebar) {
        sidebar.appendChild(form);
      } else if (window.innerWidth < 799) {
        syncDrawerWithSidebar();
      }
    }, 200));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
